import { navLinks } from "@/mocks/company";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const serviceDropdownItems = [
  { label: "各種環境試験", to: "/service" },
  { label: "ガス腐食試験 無料体験", to: "/service/free-trial" },
  { label: "受託試験のご案内", to: "/service/test-guide" },
  { label: "ガス腐食・接触抵抗 料金表", to: "/service/test-guide/gas-corrosion" },
  { label: "その他環境試験 料金表", to: "/service/test-guide/env-test" },
  { label: "試験の代表例", href: "https://www.factk.jp/factk/pdf/ex01_02.pdf" },
];

const productsDropdownItems = [
  { label: "ガス腐食試験機 KG200", to: "/products#kg200" },
  { label: "ガス腐食試験機 KG600", to: "/products#kg600" },
  { label: "接触抵抗評価装置 MS7500", to: "/products#ms7500" },
  { label: "ドライアルファ", to: "/products#dryalfa" },
  { label: "資料カタログ", to: "/products#catalog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileServiceOpen, setMobileServiceOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServiceOpen(false);
    setProductsOpen(false);
    setMobileServiceOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".service-dropdown")) {
        setServiceOpen(false);
      }
      if (!target.closest(".products-dropdown")) {
        setProductsOpen(false);
      }
    };
    if (serviceOpen || productsOpen) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [serviceOpen, productsOpen]);

  const isServiceActive = location.pathname === "/service" || location.pathname.startsWith("/service/");
  const isProductsActive = location.pathname === "/products" || location.pathname.startsWith("/products");

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-sm border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-3 shrink-0"
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <img
              src="https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/9748f6f8-69f8-4cd8-80d4-a7226d6e517f_compressed_2RwFCLTUpy3e95i1783055771_1783055772.webp"
              alt="ファクトケイ"
              className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg object-contain"
            />
            <div className="block">
              <span
                className={`font-bold text-sm sm:text-base md:text-lg tracking-wide transition-colors ${
                  scrolled ? "text-foreground-900" : "text-foreground-900"
                }`}
              >
                ファクトケイ
              </span>
              <span
                className={`ml-1 text-[10px] sm:text-xs transition-colors ${
                  scrolled ? "text-foreground-600" : "text-foreground-600"
                }`}
              >
                株式会社
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const active =
                link.path === "/service"
                  ? isServiceActive
                  : link.path === "/products"
                  ? isProductsActive
                  : location.pathname === link.path;

              if (link.path === "/service") {
                return (
                  <div key={link.path} className="service-dropdown relative flex items-center">
                    <Link
                      to="/service"
                      className={`px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                        active
                          ? "text-primary-600 font-medium bg-primary-50"
                          : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                      }`}
                    >
                      {link.label}
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setServiceOpen(!serviceOpen);
                        setProductsOpen(false);
                      }}
                      className={`w-6 h-6 flex items-center justify-center rounded transition-colors -ml-1 ${
                        active
                          ? "text-primary-600 hover:bg-primary-100"
                          : "text-foreground-500 hover:text-primary-600 hover:bg-background-100"
                      }`}
                      aria-label="サービスメニューを開く"
                    >
                      <i
                        className={`ri-arrow-down-s-line transition-transform ${
                          serviceOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {serviceOpen && (
                      <div className="absolute left-0 top-full mt-1 w-56 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-1 z-50">
                        {serviceDropdownItems.map((item) =>
                          item.href ? (
                            <a
                              key={item.href}
                              href={item.href}
                              target="_blank"
                              rel="noreferrer nofollow"
                              className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                              onClick={() => setServiceOpen(false)}
                            >
                              <span>{item.label}</span>
                              <i className="ri-arrow-right-up-line text-xs text-foreground-400" />
                            </a>
                          ) : (
                            <Link
                              key={item.to}
                              to={item.to || ""}
                              className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                              onClick={() => setServiceOpen(false)}
                            >
                              {item.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (link.path === "/products") {
                return (
                  <div key={link.path} className="products-dropdown relative flex items-center">
                    <Link
                      to="/products"
                      className={`px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                        active
                          ? "text-primary-600 font-medium bg-primary-50"
                          : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                      }`}
                    >
                      {link.label}
                    </Link>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setProductsOpen(!productsOpen);
                        setServiceOpen(false);
                      }}
                      className={`w-6 h-6 flex items-center justify-center rounded transition-colors -ml-1 ${
                        active
                          ? "text-primary-600 hover:bg-primary-100"
                          : "text-foreground-500 hover:text-primary-600 hover:bg-background-100"
                      }`}
                      aria-label="製品メニューを開く"
                    >
                      <i
                        className={`ri-arrow-down-s-line transition-transform ${
                          productsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {productsOpen && (
                      <div className="absolute left-0 top-full mt-1 w-60 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-1 z-50">
                        {productsDropdownItems.map((item) =>
                          item.href ? (
                            <a
                              key={item.href}
                              href={item.href}
                              target="_blank"
                              rel="noreferrer nofollow"
                              className="flex items-center gap-2 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                              onClick={() => setProductsOpen(false)}
                            >
                              <span>{item.label}</span>
                              <i className="ri-arrow-right-up-line text-xs text-foreground-400" />
                            </a>
                          ) : (
                            <Link
                              key={item.to}
                              to={item.to || ""}
                              className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                              onClick={() => setProductsOpen(false)}
                            >
                              {item.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                    active
                      ? "text-primary-600 font-medium bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="メニュー"
          >
            <i
              className={`ri-menu-line text-xl ${
                scrolled ? "text-foreground-900" : "text-foreground-900"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70">
          <nav className="px-4 py-3 space-y-1">
            {navLinks.map((link) => {
              const active =
                link.path === "/service"
                  ? isServiceActive
                  : link.path === "/products"
                  ? isProductsActive
                  : location.pathname === link.path;

              if (link.path === "/service") {
                return (
                  <div key={link.path}>
                    <div className="flex items-center">
                      <Link
                        to="/service"
                        className={`flex-1 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          active
                            ? "text-primary-600 font-medium bg-primary-50"
                            : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                        }`}
                        onClick={() => setMobileOpen(false)}
                      >
                        {link.label}
                      </Link>
                      <button
                        onClick={() => {
                          setMobileServiceOpen(!mobileServiceOpen);
                          setMobileProductsOpen(false);
                        }}
                        className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                          active
                            ? "text-primary-600 hover:bg-primary-100"
                            : "text-foreground-500 hover:text-primary-600 hover:bg-background-100"
                        }`}
                        aria-label="サービスメニューを開く"
                      >
                        <i
                          className={`ri-arrow-down-s-line transition-transform ${
                            mobileServiceOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                    {mobileServiceOpen && (
                      <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-background-200 pl-3">
                        {serviceDropdownItems.map((item) =>
                          item.href ? (
                            <a
                              key={item.href}
                              href={item.href}
                              target="_blank"
                              rel="noreferrer nofollow"
                              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 transition-colors"
                              onClick={() => {
                                setMobileServiceOpen(false);
                                setMobileOpen(false);
                              }}
                            >
                              <span>{item.label}</span>
                              <i className="ri-arrow-right-up-line text-xs text-foreground-400" />
                            </a>
                          ) : (
                            <Link
                              key={item.to}
                              to={item.to || ""}
                              className="block px-3 py-2 rounded-md text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 transition-colors"
                              onClick={() => {
                                setMobileServiceOpen(false);
                                setMobileOpen(false);
                              }}
                            >
                              {item.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (link.path === "/products") {
                return (
                  <div key={link.path}>
                    <div className="flex items-center">
                      <Link
                        to="/products"
                        className={`flex-1 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          active
                            ? "text-primary-600 font-medium bg-primary-50"
                            : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                        }`}
                        onClick={() => setMobileOpen(false)}
                      >
                        {link.label}
                      </Link>
                      <button
                        onClick={() => {
                          setMobileProductsOpen(!mobileProductsOpen);
                          setMobileServiceOpen(false);
                        }}
                        className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                          active
                            ? "text-primary-600 hover:bg-primary-100"
                            : "text-foreground-500 hover:text-primary-600 hover:bg-background-100"
                        }`}
                        aria-label="製品メニューを開く"
                      >
                        <i
                          className={`ri-arrow-down-s-line transition-transform ${
                            mobileProductsOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                    {mobileProductsOpen && (
                      <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-background-200 pl-3">
                        {productsDropdownItems.map((item) =>
                          item.href ? (
                            <a
                              key={item.href}
                              href={item.href}
                              target="_blank"
                              rel="noreferrer nofollow"
                              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 transition-colors"
                              onClick={() => {
                                setMobileProductsOpen(false);
                                setMobileOpen(false);
                              }}
                            >
                              <span>{item.label}</span>
                              <i className="ri-arrow-right-up-line text-xs text-foreground-400" />
                            </a>
                          ) : (
                            <Link
                              key={item.to}
                              to={item.to || ""}
                              className="block px-3 py-2 rounded-md text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 transition-colors"
                              onClick={() => {
                                setMobileProductsOpen(false);
                                setMobileOpen(false);
                              }}
                            >
                              {item.label}
                            </Link>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-3 py-2.5 rounded-md text-sm transition-colors ${
                    active
                      ? "text-primary-600 font-medium bg-primary-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}